import { JSX, useRef, useState } from 'react';

import {
	CarRental as CarRentalIcon,
	LockOpenOutlined as LockOpenOutlinedIcon,
	LockOutlined as LockOutlinedIcon,
	Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Button, ButtonGroupProps, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { ConfirmationDialog } from '~components';
import { useAuthorize } from '~features/authentication';
import { Item, ItemsInItemGroupService } from '~features/items';

import DeviceCommandEnum from '../../enums/deviceCommandEnum';
import DeviceTypeEnum from '../../enums/deviceTypeEnum';
interface CommandButtonProps {
	label: string;
	icon?: JSX.Element;
	command: DeviceCommandEnum;
	hidden?: boolean;
	confirmSubTitle?: string;
	successMessage?: string;
}

interface DeviceCommandButtonGroupProps extends ButtonGroupProps {
	itemInstance: Item;
	deviceType: DeviceTypeEnum;
}

const DeviceCommandButtonGroup = ({ itemInstance }: DeviceCommandButtonGroupProps) => {
	const itemId = itemInstance.itemReference.id.toString();
	const itemInstanceId = itemInstance.id.toString();
	const deviceType = itemInstance.deviceType;
	const itemsInItemGroupService = new ItemsInItemGroupService(itemId);

	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const { isSuperAdmin } = useAuthorize();

	const [commandType, setCommandType] = useState<DeviceCommandEnum | null>(null);
	const [commandProcessing, setCommandProcessing] = useState(false);

	const isTeltonika = (deviceType: DeviceTypeEnum) =>
		deviceType === DeviceTypeEnum.VehicleTracker ||
		deviceType === DeviceTypeEnum.VehicleTrackerCan ||
		deviceType === DeviceTypeEnum.VehicleTrackerObd ||
		deviceType === DeviceTypeEnum.VehicleTrackerPro;

	const handleConfirmClick = () => {
		if (commandType) {
			executeCommand(commandType);
		} else {
			enqueueSnackbar({
				variant: 'error',
				message: 'Unknown command',
			});
		}
	};

	/**
	 * Run the command
	 * @param types
	 */
	const executeCommand = async (command: DeviceCommandEnum) => {
		setCommandProcessing(true);

		try {
			await itemsInItemGroupService.sendCommand(itemId, itemInstanceId, command);
			// Add additional commands that are appropiate for the action.
			// For example, if we lock a device also enable immobiliser
			if (command === DeviceCommandEnum.Lock) {
				await itemsInItemGroupService.sendCommand(
					itemId,
					itemInstanceId,
					DeviceCommandEnum.EnableImmobiliser
				);
			}
			enqueueSnackbar({
				variant: 'success',
				message: buttons.find((el) => el.command === commandType)?.successMessage,
			});
		} catch (_error) {
			enqueueSnackbar({
				variant: 'error',
				message: t('somethingWentWrong'),
			});
		} finally {
			setCommandProcessing(false);
			setCommandType(null);
		}
	};

	const buttons: CommandButtonProps[] = [
		{
			icon: <RefreshIcon />,
			command: DeviceCommandEnum.Reboot,
			hidden: !(isTeltonika(deviceType) && isSuperAdmin()),
			label: t('reset'),
			confirmSubTitle: t('pleaseConfirmActionForResource', {
				action: t('reset'),
				resource: t('vehicle'),
			}),
			successMessage: t(`actionMessageWasSendToResource`, {
				action: t('reset'),
				resource: t('vehicle'),
			}),
		},
		{
			icon: <CarRentalIcon />,
			command: DeviceCommandEnum.DisableImmobiliser,
			hidden: ![DeviceTypeEnum.VehicleTrackerCan].includes(deviceType),
			label: t('ui.device.label.allowStartEngine'),
			confirmSubTitle: t('ui.device.description.disableIgnition'),
			successMessage: t('ui.device.successMessage.disableIgnition'),
		},
		{
			icon: <LockOutlinedIcon />,
			command:
				deviceType === DeviceTypeEnum.CpacBoat ?
					DeviceCommandEnum.DisableImmobiliser
				:	DeviceCommandEnum.Lock,
			label: t(deviceType === DeviceTypeEnum.CpacBoat ? 'deactivate' : 'lock'),
			confirmSubTitle: t(`ui.device.description.lock.${deviceType}`),
			successMessage: t(`ui.device.successMessage.locked.${deviceType}`),
		},
		{
			icon: <LockOpenOutlinedIcon />,
			command:
				deviceType === DeviceTypeEnum.CpacBoat ?
					DeviceCommandEnum.EnableImmobiliser
				:	DeviceCommandEnum.Unlock,
			label: t(deviceType === DeviceTypeEnum.CpacBoat ? 'activate' : 'unlock'),
			confirmSubTitle: t(`ui.device.description.unlock.${deviceType}`),
			successMessage: t(`ui.device.successMessage.unlock.${deviceType}`),
		},
	];

	return (
		<>
			<Stack direction='row' spacing={1}>
				{buttons.map((el, i) =>
					el.hidden ? null : (
						<Button
							key={i}
							startIcon={el.icon}
							loading={commandProcessing && el.command === commandType}
							onClick={() => setCommandType(el.command)}
							variant='text'
							disabled={commandProcessing && el.command !== commandType}
						>
							{el.label}
						</Button>
					)
				)}
			</Stack>
			<ConfirmationDialog
				title={buttons.find((el) => el.command === commandType)?.label}
				subTitle={buttons.find((el) => el.command === commandType)?.confirmSubTitle}
				open={commandType != null && !commandProcessing}
				onConfirm={handleConfirmClick}
				onClose={() => setCommandType(null)}
			/>
		</>
	);
};

export default DeviceCommandButtonGroup;
